import * as React from 'react';
import './index.css';
import { createInstance, I18nextProvider } from '@sailor/i18n-mach-pro';
import { getDTCode } from '@/components/ListEmptyView/util';
import { CommonEmptyCardCode, CommonEmptyCardPageSource } from './types';
import LxConstant from './LxConstant';
import resources from './locales/resources';

const SLLocation = Mach.requireModule('SLLocation');
const WMStatistics = Mach.requireModule('Statistics');
const WMRouter = Mach.requireModule('Router');
const Login = Mach.requireModule('Login');
const Raptor = Mach.requireModule('Raptor');

interface Props {
  cid: string; // cid: 埋点用
  pageSource: number; // pageSource: 判断页面来源
  code: number; // code: 状态判断码
  errorMsg?: string; // message: api返回的异常信息
  refreshPage: () => void; // refreshPage: 刷新页面回调
  extraData?: any; // extraData: 额外数据（如金刚位id）
  isShowButton?: boolean; // isShowButton: 是否展示底部按钮
}

interface IState {
  cid: string;
  is_login: number;
  region_name: string;
  cat_id: number;
}

// i18n
const i18nClient = createInstance();
i18nClient.init({
  resources,
  interpolation: {
    escapeValue: false, // react 不需要这个
  },
  react: {
    useSuspense: false,
  },
});

/*
** 通用兜底组件
*/
class CommonEmptyCardComponent extends React.PureComponent<Props, IState> {
  constructor(props) {
    super(props);
    this.state = {
      cid: this.props?.cid,
      is_login: 0,
      region_name: '',
      cat_id: this.props?.extraData?.cat_id
    };
  }

  componentDidMount() {
    this.getUserInfo();
    // Raptor监控code、pageSource
    const { code, pageSource } = this.props;
    this.reportStatus(code, pageSource);
  }

  // 获取当前用户登录状态 + 定位信息
  getUserInfo = () => {
    const isLogin = Login?.isLogin?.();
    const currentLocation = SLLocation?.getLocation() || {};
    this.setState({
      is_login: Number(isLogin),
      region_name: currentLocation?.poiName
    }, () => {
      // 曝光埋点
      const lab = this.getCommonLab();
      WMStatistics.view(this.state.cid, LxConstant.COMMON_EMPTY_CARD_MV, lab);
    });
  }

  // 获取埋点lab
  getCommonLab() {
    const { is_login, region_name, cat_id } = this.state;
    const lab: any = {
      login_status: is_login,
      region_name,
    };
    if (this.props.pageSource === CommonEmptyCardPageSource.KINGKONG) {
      lab.cat_id = cat_id;
    }
    return lab;
  }

  jumpToHomepage() {
    // code码判断跳转
    const code = this.props.code;
    const { cid, cat_id } = this.state;

    if (code === CommonEmptyCardCode.NOT_IN_MVP_AREA) {
      // 不在MVP区域
      const data: any = {
        cid
      };
      if (this.props.pageSource === CommonEmptyCardPageSource.KINGKONG) {
        data.cat_id = cat_id;
      }
      // machproalert: 数据不能用data传，需要融合到url中传递
      const jsonData = encodeURIComponent(JSON.stringify(data));
      const scheme = `sailorc://keeta.com/machproalert?bundle_name=mach_pro_sailor_c_service_address_popup&data=${jsonData}`;
      WMRouter.navigateTo(scheme, null, (result: any) => {});
    } else if (code === CommonEmptyCardCode.IN_MVP_AREA_BUT_NO_SUPPLY || code === CommonEmptyCardCode.KINGKONG_NO_SUPPLY || code === CommonEmptyCardCode.NO_DISCOUNT_SUPPLY) {
      // 在MVP区域但是没有供给
      const scheme = 'sailorc://keeta.com/home';
      WMRouter.navigateTo(scheme, null, (result: any) => {});
    } else {
      // 其他错误情况（包括code=0且无数据）
      this.props.refreshPage();
    }
    // 跳转点击埋点
    const lab = this.getCommonLab();
    WMStatistics.click(cid, LxConstant.COMMON_EMPTY_CARD_BUTTON_MC, lab);
  }

  reportStatus(code, pageSource) {
    const defaultTags = {
      container: 'Mach Pro',
      osName: Mach.env.osName ?? '',
      osVersion: Mach.env.osVersion ?? '',
      appVersion: Mach.env.version ?? '',
      platform: Mach.env.isAndroid ? 'Android' : 'iOS',
    };
    const tags = { ...defaultTags, ...{ code: code?.toString(), pageSource: pageSource?.toString() } || {} };
    Raptor?.reportMetrics({ commonEmptyCardStatus: 1 }, tags, 1);
  }

  render() {
    const code = this.props?.code;
    const pageSource = this.props?.pageSource;
    let isShowButton = this.props?.isShowButton;
    if (isShowButton != false) {
      isShowButton = true;
    }
    let dtCode = '';
    // 描述文案
    let descText;
    // 按钮文案
    let btnText;
    switch (code) {
      // 不在MVP区域
      case CommonEmptyCardCode.NOT_IN_MVP_AREA:
        descText = i18nClient.getText('mkt_nomerchant_machpro_not_mvp');
        btnText = i18nClient.getText('mkt_nomerchant_machpro_not_mvp_button');
        break;
        // 在MVP区域 06
      case CommonEmptyCardCode.IN_MVP_AREA_BUT_NO_SUPPLY:
        descText = i18nClient.getText('mkt_nomerchant_machpro_mvp');
        if (pageSource === CommonEmptyCardPageSource.KINGKONG) {
          descText = i18nClient.getText('mkt_nomerchant_filternoresult');
        }
        btnText = i18nClient.getText('mkt_nomerchant_Machpro_mvp_button');
        break;
        // 在MVP区域 07
      case CommonEmptyCardCode.NO_DISCOUNT_SUPPLY:
        descText = i18nClient.getText('mkt_nomerchant_filternoresult');
        btnText = i18nClient.getText('mkt_nomerchant_Machpro_mvp_button');
        break;
        // 错误页情况
      default:
        descText = this.props?.errorMsg || i18nClient.getText('mkt_nomerchant_error');
        btnText = i18nClient.getText('mkt_nomerchant_error_button');
        dtCode = getDTCode(code);
        break;
    }

    // 某落地页需要特殊处理的，参考以下模板
    // 一口价落地页处理
    const fixedPriceFlag = pageSource === CommonEmptyCardPageSource.FIXED_PRICE;
    if (code === 0 && fixedPriceFlag) {
      descText = i18nClient.getText('mkt_nomerchant_filternoresult');
      isShowButton = false;
    }

    return (
      <view className="container">
        <view className="content">
          <image
            style={{ width: Mach.env.screenWidth }}
            className="img"
            src="assets://common/empty.png"
          />
          <view className="titleView">
            <text className="descText">{descText}</text>;
          </view>
          {!!dtCode && <text content={dtCode} className="empty-identify-code" />}
          {isShowButton
                    && (
                      <view className="jumpBtn" onClick={this.jumpToHomepage.bind(this)}>
                        <text className="jumpBtnText">{btnText}</text>;
                      </view>
                    )}
        </view>
      </view>
    );
  }
}

const CommonEmptyCard = (props: Props) => (
  <I18nextProvider i18n={i18nClient}>
    <CommonEmptyCardComponent {...props} />
  </I18nextProvider>
);

export default CommonEmptyCard;
